<template>
  <!-- Desktop: sidebar in layout -->
  <aside
    class="main-menu hidden md:flex flex-col shrink-0 h-full p-2 transition-all duration-200"
    :class="expanded ? 'w-56' : 'w-14'"
  >
    <!-- Row 1: Logo (expanded) or Expand icon only (collapsed) -->
    <div
      class="flex items-center gap-2 mb-4"
      :class="expanded ? 'justify-between' : 'justify-center'"
    >
      <button
        type="button"
        class="flex items-center justify-center w-10 h-10 rounded-full text-surface-400 hover:bg-surface-100 hover:text-primary-500 transition-colors cursor-pointer bg-transparent border-none p-0"
        @click="toggle"
        :aria-label="expanded ? 'Collapse menu' : 'Expand menu'"
        v-tooltip.right="expanded ? 'Collapse' : 'Expand'"
      >
        <PanelLeftClose
          v-if="expanded"
          class="shrink-0 text-xl text-current"
          :size="20"
          aria-hidden="true"
        />
        <PanelLeftOpen v-else class="shrink-0 text-xl text-current" :size="20" aria-hidden="true" />
      </button>
      <RouterLink v-if="expanded" to="/products" class="shrink-0">
        <img
          src="/images/phottr-logo-dark.svg"
          alt="Phottr logo"
          class="ml-2 main-menu-logo h-8 w-auto object-contain max-w-[8rem]"
        />
      </RouterLink>
    </div>

    <!-- Nav items (desktop) -->
    <nav class="flex flex-col gap-1 flex-1">
      <RouterLink
        v-for="item in navItems"
        :key="item.to"
        :to="item.to"
        class="main-menu-link flex items-center gap-3 px-3 py-2 rounded-lg text-surface-400 no-underline hover:bg-surface-100 hover:text-primary-500 transition-colors"
        :class="[
          expanded ? 'min-h-[2.5rem]' : 'justify-center min-h-[2.5rem]',
          { '!text-primary-400': isActive(item.to) },
        ]"
        active-class="!text-primary-400"
        v-tooltip.right="expanded ? '' : item.label"
      >
        <i :class="['pi text-xl shrink-0', item.icon]"></i>
        <span v-if="expanded" class="font-medium truncate">{{ item.label }}</span>
      </RouterLink>

      <!-- Admin section (desktop) -->
      <template v-if="adminNavItems.length">
        <div
          class="mt-4 mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-surface-400"
          v-if="expanded"
        >
          Admin
        </div>
        <div v-else class="my-2 mx-3 border-t border-surface-200" />
        <RouterLink
          v-for="item in adminNavItems"
          :key="item.to"
          :to="item.to"
          class="main-menu-link flex items-center gap-3 px-3 py-2 rounded-lg text-surface-400 dark:text-surface-300 no-underline hover:bg-surface-100 hover:text-primary-500 transition-colors"
          :class="[
            expanded ? 'min-h-[2.5rem]' : 'justify-center min-h-[2.5rem]',
            { '!text-primary-400': isActive(item.to) },
          ]"
          active-class="!text-primary-400"
          v-tooltip.right="expanded ? '' : item.label"
        >
          <i :class="['pi text-xl shrink-0', item.icon]"></i>
          <span v-if="expanded" class="font-medium truncate">{{ item.label }}</span>
        </RouterLink>
      </template>
    </nav>
    <!-- Settings at bottom (desktop) -->
    <RouterLink
      to="/api-settings"
      class="main-menu-link flex items-center gap-3 px-3 py-2 rounded-lg text-surface-400 no-underline hover:bg-surface-100 hover:text-primary-500 transition-colors mt-2"
      :class="[
        expanded ? 'min-h-[2.5rem]' : 'justify-center min-h-[2.5rem]',
        { '!text-primary-400': isActive('/api-settings') },
      ]"
      active-class="!text-primary-400"
      v-tooltip.right="expanded ? '' : 'Settings'"
    >
      <i class="pi pi-cog text-xl shrink-0"></i>
      <span v-if="expanded" class="font-medium truncate">Settings</span>
    </RouterLink>
  </aside>

  <!-- Mobile: full-screen overlay when open -->
  <Teleport to="body">
    <Transition name="overlay">
      <div
        v-if="expanded"
        class="md:hidden fixed inset-0 z-50 flex"
        aria-modal="true"
        aria-label="Main menu"
        role="dialog"
        @click="close"
      >
        <div class="overlay-backdrop absolute inset-0 bg-black/50" aria-hidden="true" />
        <aside
          class="overlay-panel main-menu relative flex flex-col w-72 max-w-[85vw] h-full p-4 bg-surface-0 dark:bg-surface-950 shadow-xl"
          @click.stop
        >
          <div class="flex items-center justify-between mb-4">
            <RouterLink to="/products" class="shrink-0" @click="close">
              <img
                src="/images/phottr-logo-dark.svg"
                alt="Phottr logo"
                class="main-menu-logo h-8 w-auto object-contain max-w-[8rem]"
              />
            </RouterLink>
            <button
              type="button"
              class="flex items-center justify-center w-10 h-10 rounded-full text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-primary-500 transition-colors cursor-pointer bg-transparent border-none p-0"
              @click="close"
              aria-label="Close menu"
            >
              <X class="shrink-0 text-xl text-current" :size="24" aria-hidden="true" />
            </button>
          </div>
          <nav class="flex flex-col gap-1 flex-1">
            <RouterLink
              v-for="item in navItems"
              :key="item.to"
              :to="item.to"
              class="main-menu-link flex items-center gap-3 px-3 py-2 rounded-lg text-surface-700 dark:text-surface-300 no-underline hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-primary-500 transition-colors min-h-[2.5rem]"
              :class="{ '!text-primary-400': isActive(item.to) }"
              active-class="!text-primary-400"
              @click="close"
            >
              <i :class="['pi text-xl shrink-0', item.icon]"></i>
              <span class="font-medium truncate">{{ item.label }}</span>
            </RouterLink>

            <!-- Admin section (mobile) -->
            <template v-if="adminNavItems.length">
              <div
                class="mt-4 mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-surface-400"
              >
                Admin
              </div>
              <RouterLink
                v-for="item in adminNavItems"
                :key="item.to"
                :to="item.to"
                class="main-menu-link flex items-center gap-3 px-3 py-2 rounded-lg text-surface-700 dark:text-surface-300 no-underline hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-primary-500 transition-colors min-h-[2.5rem]"
                :class="{ '!text-primary-400': isActive(item.to) }"
                active-class="!text-primary-400"
                @click="close"
              >
                <i :class="['pi text-xl shrink-0', item.icon]"></i>
                <span class="font-medium truncate">{{ item.label }}</span>
              </RouterLink>
            </template>
          </nav>
          <RouterLink
            to="/api-settings"
            class="main-menu-link flex items-center gap-3 px-3 py-2 rounded-lg text-surface-700 dark:text-surface-300 no-underline hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-primary-500 transition-colors mt-2 min-h-[2.5rem]"
            :class="{ '!text-primary-400': isActive('/api-settings') }"
            active-class="!text-primary-400"
            @click="close"
          >
            <i class="pi pi-cog text-xl shrink-0"></i>
            <span class="font-medium truncate">Settings</span>
          </RouterLink>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-vue-next';
import { useAuth } from '@/modules/auth/auth';

const props = defineProps<{
  expanded: boolean;
}>();

const emit = defineEmits<{
  'update:expanded': [value: boolean];
}>();

const { organization, user } = useAuth();
const route = useRoute();

const expanded = computed({
  get: () => props.expanded,
  set: v => emit('update:expanded', v),
});

const navItems = computed(() => {
  const items = [
    { to: '/products', label: 'Products', icon: 'pi-box' },
    { to: '/usage', label: 'Usage', icon: 'pi-chart-bar' },
    ...(organization.value && !organization.value.personal
      ? [{ to: '/users', label: 'Users', icon: 'pi-users' }]
      : []),
    { to: '/api-settings', label: 'API', icon: 'pi-key' },
    // { to: '/external-products', label: 'External Products', icon: 'pi-external-link' },
    // { to: '/external-product-images', label: 'External Images', icon: 'pi-images' },
  ];
  return items;
});

const adminNavItems = computed(() => {
  if (!user.value?.superAdmin) return [];
  return [
    { to: '/admin/users', label: 'All Users', icon: 'pi-users' },
    { to: '/admin/users/new', label: 'New Users', icon: 'pi-user-plus' },
    { to: '/admin/transactions', label: 'Transactions', icon: 'pi-credit-card' },
    { to: '/admin/device-sessions', label: 'Sessions', icon: 'pi-mobile' },
    { to: '/admin/unclaimed-stripe-payments', label: 'Unclaimed Payments', icon: 'pi-money-bill' },
  ];
});

function toggle() {
  expanded.value = !expanded.value;
}

function close() {
  expanded.value = false;
}

function isActive(path: string): boolean {
  const { path: routePath } = route;
  if (path === '/external-product-images') {
    return routePath.startsWith('/external-product-images');
  }
  return routePath === path || routePath.startsWith(path + '/');
}
</script>

<style scoped>
/* Backdrop: fade */
.overlay-enter-active .overlay-backdrop,
.overlay-leave-active .overlay-backdrop {
  transition: opacity 0.25s ease;
}
.overlay-enter-from .overlay-backdrop,
.overlay-leave-to .overlay-backdrop {
  opacity: 0;
}
/* Panel: slide in from left */
.overlay-enter-active .overlay-panel,
.overlay-leave-active .overlay-panel {
  transition: transform 0.25s ease;
}
.overlay-enter-from .overlay-panel,
.overlay-leave-to .overlay-panel {
  transform: translateX(-100%);
}
</style>
